import React, { useState, useCallback, useEffect } from 'react';
import { 
  DropDownList, 
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent 
} from '@progress/kendo-react-dropdowns';
import { getClientsDropdown } from '../../data/clientData';
import { dropDown } from '../../data/interfaces';
import { useToast } from '../../contexts/ToastContext';
import '../Styles.css';

const TestDropdown: React.FC = () => {
  const [data, setData] = useState<dropDown[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<dropDown | null>(null);
  const [filterValue, setFilterValue] = useState('');
  const { showToast } = useToast();

  // Fetch data with filtering and virtualization support
  const fetchData = useCallback(async (filter: string = '') => {
    setLoading(true);
    try {
      const result = await getClientsDropdown(filter);
      setData(result);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showToast('Error fetching clients data', 'error');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Initial data load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle selection change
  const handleSelectionChange = (event: DropDownListChangeEvent) => {
    setSelectedValue(event.target.value);
    console.log('Selected client:', event.target.value);
  };

  // Handle filter change for remote filtering
  const handleFilterChange = (event: DropDownListFilterChangeEvent) => {
    const newFilterValue = event.filter.value;
    setFilterValue(newFilterValue);
    
    // Debounced filtering - wait 300ms after user stops typing
    clearTimeout(window.filterTimeout);
    window.filterTimeout = window.setTimeout(() => {
      fetchData(newFilterValue);
    }, 300);
  };

  return (
    <div className="p-4">
      <div className="test-dropdown-container">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">
          Kendo React DropDown Test Page
        </h1>
        
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">
            Client Dropdown with Remote Data & Virtualization
          </h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Select a Client:
            </label>
            
            <DropDownList
              data={data}
              textField="text"
              dataItemKey="value"
              value={selectedValue}
              onChange={handleSelectionChange}
              onFilterChange={handleFilterChange}
              filterable={true}
              loading={loading}
              virtual={{
                pageSize: 50,
                skip: 0,
                total: data.length
              }}
              style={{ width: '100%' }}
              popupSettings={{
                height: 400
              }}
            />
          </div>

          {selectedValue && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <h3 className="font-medium text-gray-700 mb-2">Selected Client:</h3>
              <p className="text-sm text-gray-600">
                <strong>ID:</strong> {selectedValue.value}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Name:</strong> {selectedValue.text}
              </p>
            </div>
          )}

          <div className="mt-6 space-y-2">
            <h3 className="font-medium text-gray-700">Features Demonstrated:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ <strong>Remote Data:</strong> Data fetched from /Client/ForDropdown endpoint</li>
              <li>✓ <strong>Filtering:</strong> Server-side filtering with 300ms debounce</li>
              <li>✓ <strong>Virtualization:</strong> Efficient rendering with virtual scrolling</li>
              <li>✓ <strong>Loading States:</strong> Shows loading indicator during data fetch</li>
              <li>✓ <strong>Error Handling:</strong> Graceful error handling with toast notifications</li>
              <li>✓ <strong>Clear Button:</strong> Easy way to clear the selection</li>
            </ul>
          </div>

          <div className="mt-4 text-xs text-gray-500">
            <p><strong>Current Filter:</strong> "{filterValue || '(no filter)'}"</p>
            <p><strong>Items Loaded:</strong> {data.length}</p>
            <p><strong>Loading:</strong> {loading ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    filterTimeout: number;
  }
}

export default TestDropdown;